import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePersonaStore, isKnownRoute } from '../stores/persona';
import { useImpersonationStore } from '../stores/impersonation';
import { useFeatureFlagStore } from '../stores/featureFlags';

interface PersonaRouteGuardProps {
  children: React.ReactNode;
}

export function PersonaRouteGuard({ children }: PersonaRouteGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessRoute, isAdmin } = usePersonaStore();
  const { isImpersonating } = useImpersonationStore();
  const flagsLoaded = useFeatureFlagStore(s => s.isLoaded);

  useEffect(() => {
    // Skip route guard during impersonation — the impersonation flow
    // manages navigation and persona state transitions itself
    if (isImpersonating) {
      return;
    }

    // Flags start all off, so a check before they load blocks a flagged route.
    if (!flagsLoaded) return;

    // MED-1389: HomeRoute sends a non-staff user from `/` to their own home.
    if (location.pathname === '/') return;

    // Skip check for common routes that all personas can access
    const commonRoutes = ['/no-access', '/exports'];
    if (commonRoutes.some(route => location.pathname.startsWith(route))) {
      return;
    }

    // Only redirect to no-access for known routes the persona can't access.
    // Unknown routes (404s) fall through to the catch-all NotFoundPage.
    if (isKnownRoute(location.pathname) && !canAccessRoute(location.pathname)) {
      navigate('/no-access', { replace: true });
    }
  }, [location.pathname, canAccessRoute, navigate, isAdmin, isImpersonating, flagsLoaded]);

  return <>{children}</>;
}

export default PersonaRouteGuard;
